import { FC, ReactNode, useMemo, useState } from 'react';
import { Card } from 'antd';
import { QCLabels } from '../QCLabels/QCLabels';
import { QcVisitAssignee } from './QCVisitAssignee';
import { confirmationStatuses, QcVisitStatus } from './QCVIsitStatus';
import { useLabelsHook } from 'src/pages/QC/QCLabels/useLabels.hook';
import { routesPath } from 'src/router/routes';
import { useParams } from 'src/hooks/useParams';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { selectQCTLabels } from 'src/utils/qctHelpers';
import { useAppSelector } from 'src/hooks/redux';
import { isTaskAssignedToCurrentUserSelect } from '@restore/QCTask/QCTask.selectors';
import { QcCompletedStatuses } from '@dynamika-shared/types/status';
import { prepareUrl } from 'src/utils/routerHelpers';
import { DicomButton } from 'src/pages/QC/QCVisit/components/DicomButton';
import { DxaButton } from 'src/pages/QC/QCVisit/components/DxaButton';
import { QctButton } from 'src/pages/QC/QCVisit/components/QctButton';
import { QCUploads } from 'src/pages/QC/QCUploads/QCUploads';
import { QcNotes } from '../QCNotes/QCNotes';
import { QcReadingTypes } from 'src/pages/QC/QCReadingTypes/QCReadingTypes';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { twMerge } from 'tailwind-merge';
import { useQCTaskSeriesSlice } from '@restore/QCTaskSeries/QCTaskSeries.slice';
import { QCVisitExtendedWithContextData } from '@dynamika-shared/types/visits';
import { useFeatureFlag } from '@dynamika-shared/services/featureFlags';
import { d7Format } from '@dynamika-shared/utils/date';
import QcSubjectQueries from 'src/pages/QC/QCSubjectQueries/QCSubjectQueries';

type QCVisitProps = { visit: QCVisitExtendedWithContextData };
type ContentTab = {
  key: string;
  label: string;
  children: ReactNode;
};

const contentTabs: ContentTab[] = [
  {
    key: 'Uploads',
    label: 'Uploads',
    children: (
      <div className='flex h-[calc(100vh-350px)] w-[60%] overflow-y-auto border-brightGray my-0 mx-auto'>
        <QCUploads />
      </div>
    ),
  },
  {
    key: 'Readings',
    label: 'Readings',
    children: (
      <div className='flex justify-between'>
        <Card className='h-[calc(100vh-350px)] overflow-hidden w-[57%] rounded-xl border-brightGray'>
          <QcReadingTypes />
        </Card>
        <Card className='h-[calc(100vh-350px)] overflow-hidden w-[40%] rounded-xl border-brightGray'>
          <QCLabels />
        </Card>
      </div>
    ),
  },
  {
    key: 'Notes',
    label: 'Notes',
    children: (
      <Card className='overflow-hidden w-[80%] mx-auto rounded-xl border-brightGray'>
        <QcNotes />
      </Card>
    ),
  },
];

export const QCVisit: FC<QCVisitProps> = ({ visit }) => {
  const { id: clinicalTrialId, taskId } = useParams();
  const [selectedTab, setSelectedTab] = useState(contentTabs[1].label);
  const { QUERY_MANAGEMENT_FLAG, PET_DEMO_FLAG } = useFeatureFlag();

  const seriesData = useQCTaskSeriesSlice(s => s.data);

  const viewerEnabled = useMemo(() => {
    return !!seriesData?.find(
      ({ visitDefinitionId }) => visitDefinitionId === visit.visitDefinitionId
    )?.dicomSeries?.length;
  }, [seriesData, visit.visitDefinitionId]);

  const task = useQCTaskSlice(state => state.data);
  const { currentVisitLabels } = useLabelsHook();

  // D7-3513 gates the QCT module on the STUDY having the QCT modality
  // associated, not on the current visit carrying a QCT label — so this reads
  // the clinical-trial context rather than `currentVisitLabels`.
  const isQctModalityAssociated = useClinicalTrialContextSlice(
    s => selectQCTLabels(s.groupedLabels).length > 0
  );

  // Until the context resolves every study looks like a non-QCT one, so the
  // button must not yet claim the modality is missing.
  const isClinicalTrialContextLoading = useClinicalTrialContextSlice(
    s => s.loading
  );

  const isTaskAssignedToCurrentUser = useAppSelector(
    isTaskAssignedToCurrentUserSelect
  );

  const openViewer = (search = '') => {
    if (!(clinicalTrialId && taskId)) return;
    const URL = prepareUrl(routesPath.qcTaskViewer, [clinicalTrialId, taskId]);

    window.location.replace(URL + (search ? `?${search}` : ''));
  };

  const viewerStartTitle =
    task?.status && confirmationStatuses.includes(task.status)
      ? 'View images'
      : 'DICOM QC';

  const taskIsCompleted = useMemo(
    () => QcCompletedStatuses.some(s => s === task?.status),
    [task?.status]
  );

  const isUnscheduled = visit?.type === 'UNSCHEDULED';

  const firstAcquisitionDate = (visit?.expectedDate as string)?.replace('Z', '');

  const tabs = [
    ...contentTabs,
    ...(QUERY_MANAGEMENT_FLAG
      ? [
          {
            key: 'Queries',
            label: 'Queries',
            children: (
              <Card className='overflow-hidden w-[80%] mx-auto rounded-xl border-brightGray'>
                <QcSubjectQueries />
              </Card>
            ),
          } as ContentTab,
        ]
      : []),
  ];
  return (
    <>
      <div className='flex items-center justify-between pb-2'>
        <div className='flex items-center gap-0.5'>
          <h3 className='text-[24px] font-semibold'>
            {isUnscheduled ? 'Unscheduled' : visit?.visitName}
          </h3>
          {firstAcquisitionDate && (
            <>
              <span className='text-dimGray'>•</span>
              <span className='text-1624 text-dimGray'>
                First acquisition date:{' '}
                {d7Format(firstAcquisitionDate, 'dd-MMM-yyy')}
              </span>
            </>
          )}
        </div>

        <div className='flex items-center gap-1 text-1624'>
          <QcVisitStatus
            taskIsCompleted={taskIsCompleted}
            isTaskAssignedToCurrentUser={isTaskAssignedToCurrentUser}
          />
          <QcVisitAssignee
            isTaskAssignedToCurrentUser={isTaskAssignedToCurrentUser}
            taskIsCompleted={taskIsCompleted}
          />
          <DicomButton
            isViewerDisabled={!viewerEnabled}
            openViewer={openViewer}
            viewerStartTitle={viewerStartTitle}
            dicomLabels={currentVisitLabels?.DICOM}
          />
          {PET_DEMO_FLAG && (
            <DicomButton
              isViewerDisabled={!viewerEnabled}
              openViewer={() => openViewer('tmtv')}
              viewerStartTitle={'PET Viewer'}
              dicomLabels={currentVisitLabels?.DICOM}
            />
          )}
          <DxaButton
            clinicalTrialId={clinicalTrialId}
            taskId={taskId}
            dxaLabels={currentVisitLabels?.DXA}
          />
          {isQctModalityAssociated && (
            <QctButton
              clinicalTrialId={clinicalTrialId}
              taskId={taskId}
              isQctModalityAssociated={isQctModalityAssociated}
              isLoading={isClinicalTrialContextLoading}
            />
          )}
        </div>
      </div>
      <div className='flex gap-1 mb-3'>
        {tabs?.map(({ key, label }) => (
          <Button
            key={key}
            onClick={() => {
              if (label !== selectedTab) {
                setSelectedTab(label);
              }
            }}
            className={twMerge(
              'min-w-[7rem] bg-white text-black hover:bg-waterLight py-[10px] px-[20px] ',
              label === selectedTab && 'bg-waterLight'
            )}
            type='default'
            skipBasicStyles
          >
            {label}
          </Button>
        ))}
      </div>
      <div>{tabs?.find(({ label }) => label === selectedTab)?.children}</div>
    </>
  );
};
